import { Alert, Box, Button, Grid } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { FormProvider, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';

import FormLoaderWrapper from 'src/shared/components/organisms/formLoaderWrapper';
import { DeleteConfirmationDialog } from 'src/shared/components/organisms/deleteConfirmationDialog';
import { useUnsavedChangesWarning } from 'src/shared/hooks/useUnsavedChangesWarning';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { useGlobals } from 'src/shared/contexts/appContext';
import { AutocompleteDropdown } from 'src/shared/components/form/autocompleteDropdown';
import { getPaymentMethodOptions } from 'src/shared/models/enums/paymentMethod.enum';
import { MainSettingsEnum } from 'src/shared/models/enums/mainSettings.enum';
import { getMainSetting } from 'src/shared/functions/getMainSetting';
import { getDistrictFee, updateDistrictFee } from 'src/shared/services/districtService';
import { AssociationFeeTable } from 'src/modules/associations/components/associationFeeTable';
import { FeePeriodSection } from 'src/modules/associations/components/feePeriodSection';
import { FeeLevelEnum } from 'src/modules/associations/models/feePeriod';
import { PaymentMethodOption } from 'src/modules/associations/models/associationFee';
import { DistrictFee, DistrictFeeRequest } from '../models/districtFee';

/**
 * Distriktets del av medlemsavgiften. Fliken har bara innehåll när förbundet använder
 * fördelningsbaserad avgift, eftersom distriktet annars inte har någon egen del.
 * Aviseringsmetoden styrs av förbundet och visas låst.
 */
export const DistrictFeePage = () => {
  const { t } = useTranslation();
  const { currentUser } = useGlobals();
  const params = useParams();
  const isMounted = useIsMounted();

  // Samma behörighetsspärr som på föreningens avgift, distriktets är känsligare.
  const [canEditFee, setCanEditFee] = useState<boolean>(true);
  const readOnlyMode = !canEditFee;
  const paymentMethodOptions = getPaymentMethodOptions(t);

  // Vänmedlem är ett eget belopp vid sidan av aviseringsmetodens rutor och gäller alla metoder,
  // men bara för kunder som har modulinställningen påslagen.
  const useNewFriend = getMainSetting(currentUser?.mainSettings ?? [], MainSettingsEnum.UseNewFriend, 'False') === 'True';

  const [fee, setFee] = useState<DistrictFee>();
  const [loading, setLoading] = useState<boolean>(false);
  const [openAddprofile, setOpenAddprofile] = useState<boolean>(false);

  const formMethods = useForm<DistrictFeeRequest>({
    mode: 'all',
    defaultValues: {
      paymentMethod: 0,
      sameFeeForAll: 0,
      mainAndFamily: {
        feeMainMember: 0,
        feeFamilyMember: 0,
        feeMainMemberAutogiro: 0,
        feeFamilyMemberAutogiro: 0,
      },
      familySteps: [
        { member: 'mainMember', fee: 0 },
        { member: 'familyMember1', fee: 0 },
      ],
      oneFeeForHousehold: 0,
      feeBasedOnProfile: undefined,
    },
  });

  const {
    handleSubmit,
    reset,
    watch,
    formState: { isDirty, isSubmitting },
  } = formMethods;

  const blocker = useUnsavedChangesWarning(isDirty && !isSubmitting);

  const paymentMethod = watch('paymentMethod');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const result = await getDistrictFee(params.id);

      if (isMounted()) {
        setFee(result.data);
        setCanEditFee(result.data.canEdit !== false);

        reset({
          paymentMethod: result.data.paymentMethod ?? 0,
          sameFeeForAll: result.data.sameFeeForAll,
          sameFeeForAllAutogiro: result.data.sameFeeForAllAutogiro ?? undefined,
          mainAndFamily: result.data.mainAndFamily ?? {
            feeMainMember: 0,
            feeFamilyMember: 0,
            feeMainMemberAutogiro: 0,
            feeFamilyMemberAutogiro: 0,
          },
          familySteps: result.data.familySteps ?? [
            { member: 'mainMember', fee: 0 },
            { member: 'familyMember1', fee: 0 },
          ],
          oneFeeForHousehold: result.data.oneFeeForHousehold,
          oneFeeForHouseholdAutogiro: result.data.oneFeeForHouseholdAutogiro ?? undefined,
          feeBasedOnProfile: result.data.feeBasedOnProfile,
          feeFriend: result.data.feeFriend ?? undefined,
          feeFriendAutogiro: result.data.feeFriendAutogiro ?? undefined,
        });
      }
    } catch (e) {
      if (isMounted()) ConsoleHelper.log(e);
    } finally {
      if (isMounted()) setLoading(false);
    }
  }, [isMounted, params.id, reset]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const selectedPaymentMethod = paymentMethodOptions.find((opt) => opt.value === watch('paymentMethod'));

  const onSubmit = async (data: DistrictFeeRequest) => {
    setLoading(true);

    try {
      await updateDistrictFee(params.id, data);

      if (isMounted()) {
        toast.success(t('association:feeUpdateSuccess') as string);
        fetchData();
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      if (isMounted()) {
        const errorMessage = e?.data?.messages ?? e?.data;
        toast.error(errorMessage as string);
      }
    } finally {
      if (isMounted()) setLoading(false);
    }
  };

  if (fee && !fee.distributedFee) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity='info'>{t('association:distributedFeeDistrictOff')}</Alert>
      </Box>
    );
  }

  return (
    <FormLoaderWrapper loading={loading}>
      <FormProvider {...formMethods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <DeleteConfirmationDialog
            open={blocker.state === 'blocked'}
            title={t('association:unsavedChangesTitle')}
            modaltext={t('association:unsavedChangesText')}
            value='leave'
            confirmButtonText={t('association:unsavedChangesLeave')}
            onClose={(result) => {
              if (result?.id === 'leave') {
                blocker.proceed?.();
                return;
              }

              blocker.reset?.();
            }}
          />

          <Box sx={{ p: 2 }}>
            <Grid container>
              <Grid item xs={12} mb={3}>
                <Alert severity='info'>{t('association:distributedFeeDistrictInfo')}</Alert>
                {!canEditFee && (
                  <Alert severity='info' sx={{ mt: 1 }}>
                    {t('common:feeNoPermission')}
                  </Alert>
                )}
              </Grid>

              <Grid item xs={12}>
                <FeePeriodSection
                  level={FeeLevelEnum.CountyAss}
                  associationId={Number(params.id) || 0}
                  autogiroFee={currentUser?.autogiroEnabled}
                  amountHeader={t('association:feeTableDistrictPart')}
                  showFriendFee={useNewFriend}
                  distributedFee
                  lockedProfiles
                  readOnlyMode={readOnlyMode}
                  header={() => (
                    <Grid container spacing={2} mb={2}>
                      <Grid item xs={12} md={5}>
                        <AutocompleteDropdown<PaymentMethodOption>
                          options={paymentMethodOptions}
                          disable
                          name='paymentMethod'
                          nameProp='description'
                          placeholder={t('association:paymentMethod.none')}
                          emptyLabel={t('association:paymentMethod.none')}
                          value={selectedPaymentMethod}
                          keyProp='id'
                          label={`Aviseringsmetod (${t('association:distributedFeeMethodLocked')})`}
                          callback={() => undefined}
                        />
                      </Grid>
                    </Grid>
                  )}
                >
                  {({ hasPlannedPeriods }) => (
                    <Grid item xs={12}>
                      <Grid container>
                        <Grid item xs={12}>
                          <AssociationFeeTable
                            autogiroFee={currentUser?.autogiroEnabled}
                            readOnlyMode={readOnlyMode || hasPlannedPeriods}
                            paymentMethod={paymentMethod || 0}
                            showFriendFee={useNewFriend}
                            distributedFee
                            lockedProfiles
                            amountHeader={t('association:feeTableDistrictPart')}
                            openAddprofile={openAddprofile}
                            setOpenAddprofile={setOpenAddprofile}
                          />
                        </Grid>
                      </Grid>

                      {/* Ingen knapp för att lägga till profil. Profilerna styrs av förbundet i
                        fördelningsläget, och distriktets flik visas bara då. */}
                      {!readOnlyMode && !hasPlannedPeriods && (
                        <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                          <Button type='submit' variant='contained'>
                            {t('common:button.save')}
                          </Button>
                        </Grid>
                      )}
                    </Grid>
                  )}
                </FeePeriodSection>
              </Grid>
            </Grid>
          </Box>
        </form>
      </FormProvider>
    </FormLoaderWrapper>
  );
};

export default DistrictFeePage;
